import type { id } from './id';

export const en: typeof id = {
  app: { title: 'OSB POS · Portal' },
  login: {
    doorHq: 'Head office',
    doorOutlet: 'At the outlet (PIN)',
    tenant: 'Tenant',
    email: 'Email',
    password: 'Password',
    outletCode: 'Outlet code',
    staffCode: 'Staff code',
    pin: 'PIN',
    pinNote: 'A PIN session can only <b>view</b> — changing prices & master data goes through the head-office door.',
    submit: 'Sign in',
    checking: 'Checking…',
  },
  brands: {
    kdsWarn: 'Ticket turns amber after (min)',
    kdsWarnHint: 'Applies to every outlet of this brand; an outlet may override it',
    kdsLate: 'Ticket turns red after (min)',
    title: 'Brands',
    new: 'New brand',
    empty: 'No brands yet.',
    businessType: 'Business type',
    businessTypeHint: 'Decides the shape other modules assume — F&B has tables & a kitchen, retail does not.',
    logo: 'Logo',
    logoHint: 'Shown on the login screen & cashier chrome. Empty = the brand name in capitals.',
    logoRule: 'Must be a full URL (https://…).',
    frozen: 'Code & business type cannot be changed: both are already used by receipts, the importer and other modules.',
    subtitle: 'One tenant may own several brands. Every outlet belongs to exactly one of them.',
    note:
      'The brand is the root: departments, categories, promotions and price groups all belong to it. ' +
      'Create the brand FIRST — every other master screen starts from a brand picker.',
    deactivate: 'Deactivating a brand closes ALL its outlets, menus and promotions at once.',
    footer: 'Deleting a brand is refused while any outlet, department or promotion still points at it.',
  },
  policy: {
    tab: 'Policies',
    brandHint:
      'Applies to EVERY outlet of this brand. Each outlet may override it on the Outlet screen — ' +
      'anything left blank there follows the value set here.',
    outletHint:
      'Leave blank to follow the brand. What you set here applies to THIS outlet only, and stops ' +
      'tracking later changes to the brand policy.',
    inherit: 'Follow brand',
    override: 'Set here',
    groupCash: 'Cash & drawer',
    groupBill: 'Bills & receipts',
    groupKitchen: 'Kitchen',
    groupGuest: 'Guest calls',
    cashVariance: 'Cash variance tolerance (Rp)',
    cashVarianceHint:
      'Variance up to this amount may be closed without a reason. 0 = every rupiah must be ' +
      'explained. The variance is still computed and still reaches the day-close record — only ' +
      'the reason requirement is relaxed.',
    drawerReview: 'Flag drawer opening #',
    drawerReviewHint:
      'From this many openings in one session onward, each is flagged for supervisor review. The ' +
      'pattern is what matters, not a single occurrence.',
    reprintWindow: 'Reprint window (minutes)',
    reprintWindowHint:
      'Reprinting outside this window is still allowed, but flagged for review. A torn receipt ' +
      'reprinted a minute later is normal; three hours later is not.',
    removeSent: 'Allow removing items already sent to the kitchen',
    removeSentHint:
      'Off = rejected; correct it with a reasoned void. On = allowed, but requires a supervisor ' +
      'PIN and a reason, and is always flagged for review.',
    removeSentWarning:
      'Turning this on does not open "delete freely" — the food is already cooked, so what is ' +
      'discarded is stock already consumed. The audit trail records that the item did reach the ' +
      'kitchen, so "how much did we cook and then bin?" stays answerable.',
    removeSentWindow: 'Time limit (minutes)',
    removeSentWindowHint:
      'Once this long has passed since the item reached the kitchen, removal is no longer offered ' +
      '— correct it with a void/refund instead. Set 0 for no time limit.',
    removeSentOff: 'off — items already in the kitchen cannot be removed',
    windowMeans: 'refused once it has been {{n}} minutes in the kitchen',
    forever: 'no limit',
    minutes: 'minutes',
    printZeroMod: 'Print zero-price modifiers',
    printZeroModHint:
      'Guest receipts only — kitchen tickets always show them. Priced modifiers always print, ' +
      'whatever this is set to.',
    callCooldown: 'Cooldown between calls (seconds)',
    callCooldownHint: 'One table cannot call again until this has elapsed.',
    callSpam: 'Suspend after … spam calls',
    callSpamHint: 'Counted within the suspension window, not for all time.',
    callSuspend: 'Suspension length (minutes)',
    callSuspendHint: 'For this long, that table QR cannot call at all.',
    callNeedsOpen: 'Only while the outlet is open',
    callNeedsOpenHint:
      'A static QR can be photographed; without this, a former guest could call a waiter from home at midnight.',
  },
  roles: {
    title: 'Roles & Permissions',
    subtitle: 'Permissions attach to a ROLE, and roles map to POSITIONS — never to a person.',
    new: 'New role',
    empty: 'No roles yet. Create one first — without a role, staff hold no permissions at all.',
    note: 'The order is: create the role → tick its permissions → map it to a position → only then can staff use it. Staff point at a position; the position points at a role.',
    permCount: 'Permissions',
    noPerm: 'none',
    nPerm: '{{n}} permissions',
    positions: 'Positions',
    noPosition: 'not mapped yet',
    positionHint: 'Type a position code then Enter. A role mapped to no position is a role whose permissions never reach anyone.',
    pickRole: 'Pick a role',
    permOf: 'Permissions for',
    permBelow: 'Permissions & positions are edited in the card below — both take effect immediately, without waiting for Save.',
  },
  floor: {
    title: 'Outlet Floor',
    tier: {
      column: 'Compute mode',
      server: 'Via server',
      tablet: 'Standalone tablet',
    },
    printer: {
      column: 'Printer',
      set: 'Set up',
      edit: 'Edit',
      title: 'Printer profile',
      transport: 'Connection',
      net: 'Network (LAN)',
      host: 'Printer IP',
      port: 'Port',
      paper: 'Paper width',
      codepage: 'Codepage',
      codepageHint: 'PC437 (0) is safe for Indonesian text. A wrong profile prints garbled paper while the screen looks fine.',
      model: 'Model',
      cutter: 'Has cutter',
      drawer: 'Can kick drawer',
      macHint: 'Take it from the printer self-test slip. Colons are added for you.',
      macBad: 'MAC must be 12 hex digits, e.g. 7C:6C:F0:E2:24:45',
      btHint: 'The Bluetooth printer must already be paired in this tablet\'s Android settings, and the print agent installed. Without that it fails looking like a broken printer.',
      netHint: 'Static IP or DHCP reservation required. A moving IP stops printing with nobody having changed a setting.',
    },
    lastSync: 'Last SYNC',
    neverSynced: 'never',
    behind: 'behind',
    pickOutlet: 'Pick an outlet first — areas, tables, and devices all belong to one outlet.',
    delete: 'Delete',
    roleHint: '⭐ `cashier` = till: may take payment and hold the cash session — one outlet may have several. `pos_terminal` = order-entry station: opens bills and sends to the kitchen, but CANNOT settle payment even on identical hardware.',
    mayTender: 'may take payment',
    deleteTable: 'Delete this table?',
    deleteDevice: 'Delete this device?',
    deleteDeviceHint: 'For a mistyped device that was never used. Broken or lost ones should be DEACTIVATED — old trails stay explainable.',
    pair: 'Pair',
    pairTitle: 'Device pairing code',
    pairHint: 'Type this code on the tablet (Pair Device screen). Valid 15 minutes, single use, and not viewable again after this dialog closes.',
    deleteArea: 'Delete this area?',
    areaInUse: 'used by {{n}} tables',
    areas: 'Areas',
    areaName: 'Area name (e.g. Floor 1)',
    areaHint: 'Areas first: a table cannot exist without its parent. Type a name then Enter.',
    noArea: 'no areas yet',
    areaFirst: 'Create an area in the card above first.',
    tables: 'Tables',
    noTable: 'no tables yet',
    seats: 'Seats',
    kind: 'Kind',
    tablesRequired: 'This outlet is table_service, so the readiness gate requires at least ONE table before a cash session can open.',
    devices: 'Devices',
    noDevice: 'no devices yet',
    role: 'Role',
    deviceHint: 'Devices are addressed by CODE, not IP — IPs change, transaction trails do not. The POS terminal must be registered before a cash session can open.',
  },
  outlets: {
    taxExempt: 'Outlet WITHOUT tax',
    taxExemptHint: 'Turn on ONLY if this outlet genuinely collects no tax. The readiness gate requires a tax assignment OR this declaration — silently empty is not accepted, because that is how receipts go out without tax with nobody deciding it.',
    readiness: 'Readiness',
    readinessReady: 'Ready to sell',
    readinessBlocked: '{{n}} blocker(s)',
    readinessOk: 'All readiness gates pass — this outlet may open a cash session.',
    readinessFix: 'Open {{layar}} →',
    readinessPassed: 'Passing ({{n}})',
    allowReopenCash: 'Allow reopening cash-settled bills',
    allowReopenCashHint:
      'Cashiers may reverse payment on a bill already settled in CASH, while its cash session is ' +
      'still open and the sale is not yet posted. Off = the answer is a refund.',
    allowReopenCashWarning:
      'Cash is the only tender that can go straight into a pocket. Turning this on means a cash ' +
      'bill can be reopened and re-closed with a different payment method or with a discount — ' +
      'leaving no trace outside the system. Every occurrence is logged and flagged for review.',
    tabKitchen: 'Kitchen',
    kdsOverride: 'Use its own thresholds',
    kdsOverrideHint: 'Turn off to follow the brand setting',
    kdsInherit: 'Following the brand thresholds — changing them on the Brand screen applies here too',
    capabilities: 'Outlet capabilities',
    capabilityNotYet: 'not active yet',
    capabilityHint:
      'THIS is what the engine reads — the preset only sets the initial values. Switching off ' +
      '`kitchen_routing` removes the must-send-before-billing rule; switching it on requires the ' +
      'outlet to have at least one station.',
    preset: 'Outlet kind',
    presetHint: 'Switches on the right capabilities (kitchen tickets, barcode, stock deduction) and fills in the sale mode. Filtered by the brand\'s business type.',
    presetPickBrand: 'Pick a brand first',
    tabIdentity: 'Identity',
    tabOps: 'Operations',
    tabFiscal: 'Pricing & tax',
    tabAdmin: 'Administration',
    taxAfterSave: 'Applicable taxes are set after the outlet is saved — assigning a tax needs an outlet that exists.',
    title: 'Outlets',
    new: 'New outlet',
    code: 'Code',
    name: 'Name',
    saleMode: 'Sale mode',
    saleModeHint: 'Decides the shape of the cashier screen. No default — it must be answered deliberately.',
    timezone: 'Time zone',
    address: 'Address',
    cutoverHint:
      'The day cutover is the root of the business date: a restaurant closing at 02:00 means a 01:30 ' +
      'sale belongs to the PREVIOUS day. Getting it wrong shifts every daily report.',
    subtitle: 'Click a row to edit it. Sale mode can be switched straight from its column.',
    needBrand: 'Create the brand first — an outlet always belongs to one brand.',
    brandFixed: 'Cannot be changed later: moving brand replaces every menu, price and promotion.',
    frozen: 'Code & brand cannot be changed. An outlet that moves brand is a new outlet, not an edit.',
    cutover: 'Day cutover',
    sentAt: 'Data sent',
    neverSent: 'never',
    offlineLimit: {
      title: 'Offline selling limit',
      days: 'days',
      hint: 'After this many days without a connection, tills hold NEW bills; an open bill can still be completed. Applies to every outlet in this tenant.',
    },
    sendData: 'Send Data',
    sendPickBrand: 'Pick a brand',
    sendButton: 'Send Data',
    sentTo: 'Sent to {{n}} outlet(s) — the SYNC button is now lit at the tills',
    sendHint:
      'This changes nothing at the till yet. The SYNC button lights up on their screen and the ' +
      'cashier presses it — so prices never change mid-queue. A newly opened cash session pulls ' +
      'it automatically.',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    empty: 'No outlets yet.',
    readOnly: 'This session is <b>view only</b>. To change settings, sign in through the head-office door.',
    tableService: 'Table service (start from a table)',
    quickService: 'Quick service (straight to the menu)',
    tableServiceShort: 'Table service',
    quickServiceShort: 'Quick service',
  },
  nav: { title: 'Navigation', grpOutlet: 'Network', grpMaster: 'Master data', grpReport: 'Reports' },
  master: {
    tab: 'Menu master',
    departments: 'Departments',
    categories: 'Categories',
    products: 'Products',
    prices: 'Prices',
    code: 'Code',
    name: 'Name',
    channel: 'Channel',
    price: 'Price',
    validFrom: 'Valid from',
    validTo: 'Until',
    running: 'running',
    inactive: 'inactive',
    addPrice: 'Add price',
    pickDepartment: 'Pick a department first.',
    pickCategory: 'Pick a category first.',
    pickProduct: 'Pick a product first.',
    noPrice: 'This product has no price — the cashier cannot sell it.',
    priceMovedHint: 'Price entry moved to the Prices screen — there you can price across every channel and every group at once.',
    priceRule:
      'Changing a price does NOT overwrite the old one: the running row is closed on the effective date, then a new row is created. ' +
      'That is what keeps "what was the price last month?" answerable.',
  },
  routing: {
    counter: 'picked up at the counter — no ticket',
    counterHint:
      'Use `counter` for goods nobody prepares (cigarettes, bottled drinks, merchandise): route the ' +
      'product there and the order passes without issuing a ticket. ⛔ A product that has NOT been ' +
      'routed is still refused — the two are deliberately different.',
    tab: 'Kitchen routing',
    outlet: 'Outlet',
    stations: 'Print / KDS stations',
    station: 'Station',
    kind: 'Kind',
    code: 'Code',
    name: 'Name',
    isDefault: 'Fallback',
    addStation: 'New station',
    stationSubtitle: 'Where tickets physically come out at this outlet. Click a row to edit it.',
    defaultHint:
      'The fallback station receives products that have no route. With no fallback at all, sending an ' +
      'order is REFUSED — deliberately, because a lost ticket never makes a sound.',
    product: 'Product',
    routes: 'Destinations for this product at the selected outlet',
    pickOutlet: 'Pick an outlet first.',
    pickProduct: 'Pick a product first.',
    noStation: 'This outlet has no station. Selling still works, but no kitchen ticket is printed.',
    unroutedInfo: 'Some products have no destination — but this outlet\'s default station prints tickets, so anything missed still shows up in the kitchen.',
    unroutedBlocker: '⚠️ Products without a destination will not reach this outlet\'s kitchen. Selling still works — the kitchen just never gets the ticket.',
    perOutlet:
      'Routing belongs to the OUTLET, not the product: the same item can print at a different station ' +
      'elsewhere — what decides is the distance from the prep table, not the kind of food.',
    noRoute: 'No destination at this outlet yet. Without a route (or fallback station) this item never reaches the kitchen — the sale still goes through.',
    remove: 'Detach',
  },
  product: {
    detail: 'Product detail',
    taxGroupForward: '⚠️ Applies to FUTURE sales only. Receipts already issued carry their own tax and do not change.',
    receiptName: 'Receipt name',
    receiptHint: 'Receipt paper is 32–42 characters — what gets cut is usually the distinguishing part.',
    description: 'Description',
    sortOrder: 'Display order',
    control: 'Controls',
    discountAllowed: 'Discountable',
    zeroPriceAllowed: 'Zero price allowed',
    returnable: 'Returnable',
    controlHint:
      'These ENFORCE, they are not notes: without "discountable", cigarettes and bottled drinks can be ' +
      'discounted silently — never as an error, only as a margin that quietly thins.',
    availability: 'Availability',
    dateRange: 'Valid period',
    timeRange: 'Time window',
    days: 'Active days',
    alwaysHint: 'Empty = always available. Do not use 00:00–23:59 to mean the same thing.',
    barcodes: 'Barcodes',
    barcodeAdd: 'Add barcode',
    barcodeHint: 'More than one is fine — different packaging carries a different barcode.',
    save: 'Save',
    saved: 'Saved',
  },
  structure: {
    tab: 'Menu structure',
    brand: 'Brand',
    perBrand:
      'Departments & categories belong to the BRAND: each brand has its own menu structure. Create the ' +
      'department first, then its categories — there is no auto-created "Other" bucket.',
    pickBrand: 'Pick a brand first — a menu structure always belongs to one brand.',
    depSubtitle: 'The top grouping: kitchen, bar, retail. Set up once, rarely changed.',
    katSubtitle: 'What a department contains. Every category has exactly one department — nothing dangles.',
    newDepartment: 'New department',
    newCategory: 'New category',
    noDepartment: 'This brand has no departments yet.',
    noCategory: 'No categories in this brand yet.',
    needDepartment: 'Create the department first — a category may not dangle without a parent.',
    sortOrder: 'Order',
    codeRule: 'Letters, digits, - and _ only. No spaces.',
    codeFixed: 'The code is used on receipts & reports — it cannot be changed once created.',
    codeLocked: 'The code cannot be changed: it is already printed on old receipts and used in reports.',
    activeVsOutlet:
      'Inactive here means off in EVERY outlet of this brand. To switch it off in a single outlet, ' +
      'use the Outlet availability screen.',
  },
  priceGroup: {
    allChannels: 'All channels',
    replicateBtn: '→ all channels',
    replicateTitle: 'Apply this price to EVERY channel?',
    replicateWarning:
      'Six channels at once, including GoFood/Grab/Shopee. Delivery platforms take 20–30% ' +
      'commission — matching their price to dine-in means that gap comes out of your margin. ' +
      'Every channel will be shown afterwards so the result can be checked.',
    replicateOkBtn: 'Apply to all',
    replicateOk: 'Applied to {{n}} channels',
    replicatePartial: 'Partly FAILED: {{list}} — the rest are already written, do not redo all of it',
    target: 'Target',
    generalTarget: 'General price (all outlets)',
    generalHint:
      'The base price used by every outlet that belongs to no group. Fill this in first; groups ' +
      'only override it for the outlets pointing at them.',
    channels: 'Channels',
    fillTitle: 'Set group prices',
    pickToFill: 'Pick a group to price',
    fillHint:
      'Products left blank follow the general price — that is a valid state, not an oversight. ' +
      'Only fill in what actually differs for this group.',
    basePrice: 'General price',
    groupPrice: 'Group price',
    noBasePrice: 'no price yet',
    followsBase: 'follows general',
    noProduct: 'This brand has no products yet. Build its menu on the Products screen first.',
    title: 'Price groups',
    new: 'New price group',
    empty: 'This brand has no price group yet.',
    members: 'Outlets in it',
    noMember: 'not used by any outlet',
    subtitle: 'One tariff shared by several outlets — malls, shophouses, airports.',
    assignHint: 'An outlet points at its group on the Outlets screen, not here: the column belongs to the outlet.',
    none: 'No group (standard price)',
    switchHint: 'Switching group moves EVERY price of this outlet at once.',
    note:
      'A price is written ONCE to its group instead of copied to each outlet. What wins when the ' +
      'cashier looks up a price: an OUTLET price, then the GROUP price, then the standard price.',
  },
  tax: {
    tabRate: 'Rate & regime',
    tabBase: 'Base & validity',
    doubleTax:
      'VAT & PB1 are EXCLUSIVE: a product charged VAT must not also be charged PB1. Split them into ' +
      'different fiscal classes — restaurant food & service to the PB1 group, packaged goods to VAT.',
    conflictsWith: 'conflicts with {{code}}',
    kind: 'Regime',
    kindHint: 'The REGIME, not the tax name. PB1 = local_tax (regional, not creditable); VAT = vat.',
    rate: 'Rate',
    rateHint: 'Enter a PERCENTAGE — "11" means 11%. Stored as the fraction 0.110000.',
    rateFrozen:
      'The rate CANNOT be changed. Overwriting it would change how OLD reports are recomputed, and ' +
      'the difference surfaces in the tax return. The correct way: end this code on its date, then ' +
      'create a new code with the new rate.',
    sequence: 'Order',
    sequenceHint: 'Lower is computed first. SC 10 · PB1 20 · VAT 30.',
    base: 'Taxable base',
    baseHint: 'What it is charged ON TOP OF. Indonesian F&B PB1: net_sales + service_charge.',
    baseRequired: 'Pick at least one base component.',
    inclusive: 'Price already includes this tax',
    inclusiveHint: 'Not "includes other taxes" — the selling price itself already contains this tax.',
    startsOn: 'Valid from',
    endsOn: 'Ends on',
    endsHint: 'Leave empty while it is running. Setting a date ends it — it does not delete it.',
    validity: 'Validity',
    codes: 'Tax codes',
    codesSubtitle: 'Which taxes exist and at what rate. Click a row to pick its outlets.',
    newCode: 'New tax code',
    outlets: 'Applies at outlets',
    pickCode: 'Pick a tax code to choose its outlets.',
    outletHint:
      'PB1 is a REGIONAL tax — its rate follows the outlet\'s city. A chain across cities normally ' +
      'has several PB1 rates running at once.',
    tab: 'Tax & service',
    group: 'Tax class',
    groupHint: 'Decides which taxes apply. STANDARD = all of them; cigarettes & vouchers get their own class.',
    groups: 'Tax groups',
    groupsSubtitle: 'The fiscal class of a product. Click a row to pick which tax codes apply.',
    newGroup: 'New tax group',
    codesApplied: 'Tax codes that APPLY',
    noTaxIntended: 'Intentionally tax-free',
    noCode: 'no tax at all',
    pickGroup: 'Pick a group to set its tax codes.',
    pickCodesHint: 'After saving, tick its tax codes in the right panel — only ticked codes apply.',
    channelLimit: 'Channel limit',
    channels: 'Channels',
    allChannels: 'all channels',
    channelHint:
      'Empty = applies in EVERY channel. Pick channels only when the tax really is limited — ' +
      'some outlets charge service only on dine-in, others (Arena included) charge it on takeaway ' +
      'too. Leave it empty when the tax is not limited.',
    note:
      'Two different axes: the GROUP belongs to the goods (cigarettes without service charge), the ' +
      'CHANNEL belongs to how they are sold (takeaway without service charge). New tax codes are ' +
      'CREATED here, but an existing rate cannot be changed — it is ended and replaced by a new code.',
  },
  preset: {
    resto_full: 'Full-service restaurant',
    resto_quick: 'Quick-service restaurant',
    coffee_shop: 'Coffee shop',
    warteg: 'Small eatery',
    minimarket: 'Minimarket / grocery',
    toko_bangunan: 'Building supplies',
    resto_kertas: 'Restaurant — handwritten tickets',
  },
  capability: {
    kitchen_routing: 'lines must reach the kitchen before billing',
    barcode_lookup: 'items found by barcode, not a menu grid',
    weighing_scale: 'quantity comes from a scale',
    tiered_price: 'quantity-tiered pricing (wholesale)',
    recipe_backflush: 'stock depletes via RECIPE',
    direct_stock_deduct: 'stock depletes DIRECTLY per SKU',
  },
  payments: {
    scope: 'Applies to',
    allBrands: 'All brands',
    scopeHint: 'Leave blank so it applies to every brand — that is right for cash & QRIS. Pick a brand only for methods specific to it (vouchers, gift cards).',
    tab: 'Payment methods',
    title: 'Payment methods',
    subtitle: 'Without at least one, an outlet CANNOT sell',
    new: 'New payment method',
    empty: 'No payment methods yet.',
    required: 'An outlet needs at least one payment method — the readiness gate blocks it otherwise',
    tenderType: 'Economic type',
    tenderHint: 'Decides how the ERP books it. Cannot be changed after creation',
    typeLocked: 'Economic type cannot be changed — it would move yesterday’s transactions to another category',
    tender: {
      cash: 'Cash',
      clearing: 'Clearing (card/QRIS)',
      liability_release: 'Liability release (voucher/deposit)',
      receivable: 'Receivable (bill later)',
      non_tender: 'Not a payment',
    },
    tenderWhy: {
      cash: 'Physical money in the drawer. Counted at close, and the variance speaks up',
      clearing: 'Money not in hand yet — the provider settles later, often minus MDR',
      liability_release: 'Guest pays with what was already paid earlier. This settles our liability, not new revenue',
      receivable: 'Guest has not paid at all. This is a receivable, and it must be collected',
      non_tender: 'Not money: comps, tasting, or corrections. Still recorded so it stays visible',
    },
    opensDrawer: 'Opens drawer',
    drawerHint: 'The drawer is a payment-method decision, not a printer trait. QRIS does not open it',
    viaEdc: 'Via EDC terminal',
    viaEdcHint: 'Cashier must copy the approval code from the EDC slip. Without it the bank deposit cannot be matched to receipts.',
    requiresReference: 'Reference required',
    referenceHint: 'The cashier cannot close without an approval/EDC number',
    provider: 'Settlement provider',
    providerHint: 'Bank/provider that settles — used to match deposits',
    mdr: 'MDR (%)',
    mdrHint: 'Provider fee. NOT a sales discount — revenue stays whole',
    erpNote: 'Account mapping happens in the ERP (pos_tender_map), not here',
  },
  subscription: {
    tab: 'Subscription',
    title: 'Subscription',
    plan: 'Plan',
    validFrom: 'Valid from',
    validTo: 'Ends',
    noEnd: 'No end date',
    features: 'Entitlements',
    feature: 'Feature',
    limit: 'Limit',
    unlimited: 'Unlimited',
    noFeature: 'This plan does not unlock any feature yet.',
    none: 'No active subscription',
    contactOsb: 'Contact OSB to activate or renew.',
    expiring: 'Subscription ends in {{n}} days',
    expired: 'Subscription ended {{n}} days ago',
    readOnly: 'This page is informational. Plan changes are made by OSB.',
    loadFailed: 'Could not load subscription info',
  },
  staff: {
    tab: 'Staff & roles',
    title: 'Staff',
    subtitle: 'Who may sign in to the register, and how far',
    new: 'New staff',
    empty: 'No staff yet.',
    code: 'Staff code',
    position: 'Position',
    chain: 'Permissions reach a person through: staff → position → role → permission. Change permissions on the role, not per person.',
    positionHint: 'Left empty = this person has no permissions yet',
    noPosition: 'No position',
    positionUnmapped: 'Position not mapped to a role',
    hrSource: 'Source',
    source: { local: 'Created in portal', external: 'From HR' },
    k8: 'The POS stores no salary, ID card, or photo — those belong to the HR app.',
    pin: 'PIN',
    setPin: 'Change PIN',
    setPinFor: 'Change PIN — {{name}}',
    pinHint: '4–8 digits. The old PIN cannot be shown — only its secure digest is stored.',
    roles: 'Roles & permissions',
    permission: 'Permission',
    noRole: 'No roles yet. Without a role, positions cannot be mapped and staff have no permissions.',
  },
  modifier: {
    tab: 'Modifiers',
    groups: 'Modifier groups',
    groupsSubtitle: 'Set up once, used by many products',
    newGroup: 'New group',
    noGroup: 'No modifier groups yet.',
    attachHint: 'Groups are defined here; choosing which group applies to a product is done on the Products screen.',
    rule: 'Selection rule',
    required: 'Requires {{n}}',
    optional: 'Optional',
    max: 'Max {{n}}',
    maxNone: 'No max',
    minSelect: 'Minimum selected',
    minHint: '≥1 means REQUIRED — a line cannot be saved without it',
    maxSelect: 'Maximum selected',
    maxHint: '0 = unlimited',
    freeText: 'Free text',
    freeTextHint: 'e.g. "sauce on the side"',
    freeTextNeverPriced: 'Free text never adds price — it changes how it is made, not what is paid.',
    pickGroup: 'Pick a group to see its options.',
    optionsOf: 'Options — {{name}}',
    options: 'Options',
    newOption: 'New option',
    noOption: 'This group has no options yet.',
    requiredWarning: 'This group is REQUIRED. Without options, the cashier cannot save a line that uses it.',
    kind: 'Kind',
    kindHint: 'Decides whether it adds price and deducts stock',
    kinds: {
      free: 'Free',
      priced: 'Adds price',
      priced_stock: 'Adds price + deducts stock',
    },
    priceDelta: 'Price delta',
    priceHint: 'Ignored for the Free kind',
    forProduct: 'Modifiers for this product',
    applies: 'Applies',
    noGroupYet: 'No modifier groups yet. Create one on the Modifiers screen.',
    attachWarning: 'A REQUIRED group will hold the cashier until answered. Make sure it has options.',
  },
  crud: {
    search: 'Search…',
    empty: 'No data yet.',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Saved',
  },
  products: {
    kindSimple: 'Simple',
    kindCombo: 'Combo / set',
    kindHint: '⛔ Cannot be changed after creation: old order lines would disagree with the master. Pick COMBO if it is made of other products — the composer appears in the product detail.',
    tab: 'Products',
    kind: 'Kind',
    new: 'New product',
    empty: 'No products in this category yet.',
    subtitle: 'Click a row to open its detail: controls, availability, barcodes and prices.',
  },
  availability: {
    tab: 'Outlet availability',
    outlet: 'Outlet',
    available: 'Available',
    inherited: 'follows parent',
    note:
      'By default the whole brand menu is available at every outlet; only EXCEPTIONS are stored here. ' +
      'Turning off a department or category closes everything inside it at that outlet.',
  },
  combo: {
    title: 'Combo composition',
    qtyPerOption: 'How many',
    qtyPerOptionHint:
      'How many come out when THIS option is picked. "Roti canai chicken 2, beef 1" goes here — ' +
      'not by widening min/max, which hands the number back to the waiter memory.',
    sharedBy: 'used by {{n}} set menus',
    reusePick: 'Use an existing group',
    reuseAdd: '+ Use this group',
    detach: 'Detach',
    detachGroup: 'Detach this group from this set menu? The group stays for other set menus.',
    editLinkHint: 'This set menu only. Other set menus using the same group are unaffected.',
    fixed: 'always included',
    fixedPick: 'Component always included',
    addFixed: '+ Fixed component',
    editGroup: 'Edit',
    editHint:
      'min is enforced by the server when sending to the kitchen. Raising it on a package already ' +
      'sold: old bills stay valid, new ones demand more.',
    preview: 'This is what the till will show',
    previewFixed: '— {{item}} (automatic, the cashier does not choose)',
    previewChoice: '— cashier picks {{n}} of {{total}}',
    impossible: 'IMPOSSIBLE: fewer options than min',
    hint: 'A group is one guest choice (e.g. "Pick a drink"). A FIXED component = a group with one option, min=max=1. ⚠️ Upcharge is a DIFFERENCE, not the component price.',
    empty: 'This product is a combo but has no group yet — it would sell as an EMPTY package: the guest pays, the kitchen receives nothing.',
    addGroup: 'Add group',
    groupCode: 'Group code',
    groupName: 'Group name',
    min: 'Min',
    max: 'Max',
    exactly: 'pick exactly {{n}}',
    range: 'pick {{min}}–{{max}}',
    component: 'Component',
    qty: 'Qty',
    upcharge: 'Upcharge',
    default: 'Default',
    addOption: 'Add option',
    noOption: 'No options yet — a group without options cannot be picked',
    deleteGroup: 'Delete this group and its options?',
  },
  promoTarget: {
    level: 'Level',
    what: 'Applies to',
    department: 'Department',
    category: 'Category',
    product: 'Product',
    pick: 'Pick a department / category / product',
    add: 'Add target',
    none: 'No targets yet',
    emptyMeans: '⚠ With no target, this promo applies to EVERY discountable line — exactly like an order-scoped promo. Add a target to narrow it.',
    narrows: 'The promo applies only to the lines below. One department row already covers every category and product under it.',
  },
  history: {
    title: 'Change history',
    when: 'When',
    action: 'Action',
    actor: 'By',
    changed: 'What changed',
    empty: 'No changes recorded for this row yet',
  },
  consoleUsers: {
    tab: 'Console users',
    email: 'Email',
    name: 'Name',
    role: 'Role',
    staff: 'Staff',
    lastLogin: 'Last sign-in',
    never: 'Never',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    locked: 'Locked',
    add: 'Add user',
    create: 'Issue',
    created: '{{email}} can now sign in',
    password: 'Initial password',
    weak: 'At least {{n}} characters',
    reset: 'Reset password',
    resetFor: 'Reset password for {{email}}',
    resetHint:
      'The new password takes effect immediately, and the account is unlocked if it was locked. ' +
      'Hand it over through a safe channel, then ask them to change it themselves.',
    resetOk: 'Password reset',
    resetUnlocked: 'Password reset — the account was unlocked too',
    deactivate: 'Deactivate',
    activate: 'Activate',
    roleName: { owner: 'Owner', admin: 'Administrator', manager: 'Manager', viewer: 'Viewer' },
    ownerProtected: 'Owner account — only an owner can change it',
    roleHint:
      'Viewer reads only · Manager may change master data · Administrator also manages users '
      + '(⛔ except Owner accounts) · Owner may do everything. ' +
      '⛔ The last active owner cannot be deactivated — otherwise this tenant loses its only way ' +
      'in, with no way back.',
    vsStaff:
      'These are the people who sign in to the PORTAL with an email and password. Cashiers, ' +
      'waiters and cooks live on the Staff screen — they sign in with a staff code and PIN, and ' +
      'they are genuinely a different population. One person may appear in both.',
  },
  password: {
    button: 'Change password',
    title: 'Change my password',
    current: 'Current password',
    next: 'New password',
    repeat: 'Repeat new password',
    mismatch: 'The repeat does not match yet',
    weak: 'At least {{n}} characters',
    save: 'Save',
    ok: 'Password changed',
    hint:
      'The current password is required so a leaked session cannot permanently take over this ' +
      'account. Sessions already running stay valid until they expire.',
  },
  /** Notifikasi keluar (0068) — kotak masuk & tujuan per outlet. */
  notify: {
    tab: 'Notifications',
    tabInbox: 'Inbox',
    tabTargets: 'Recipients',
    colWhen: 'Time',
    colOutlet: 'Outlet',
    colKind: 'Kind',
    colMessage: 'Message',
    colDelivery: 'Delivery',
    colChannel: 'Channel',
    colAddress: 'Address',
    colLabel: 'Note',
    colKinds: 'Subscribed',
    colHealth: 'Status',
    allOutlets: 'All outlets',
    addTarget: 'Add recipient',
    targetTitle: 'Notification recipient',
    addressHelp:
      'Telegram: chat_id. Send any message to the notification bot, then ask an admin for its chat_id.',
    needKind: 'Pick at least one kind',
    test: 'Send test',
    testOk: 'Sent — check Telegram',
    testNone: 'Nothing was sent; see the Status column',
    healthy: 'Healthy',
    noTarget: 'No recipients',
    sent: '{{n}} sent',
    pending: '{{n}} queued',
    failed: '{{n}} failed',
    noChannelTitle: 'No channel can send yet',
    noChannelDesc:
      'Events are still recorded in the inbox, but nothing leaves the server. '
      + 'Ask the operator to configure the Telegram bot token.',
    kind: {
      shift_variance: 'Cash variance',
      day_closed: 'Day closed',
      sale_rejected: 'Sale rejected',
    },
  },
  /** Stok, bahan & resep (0069). */
  stock: {
    tab: 'Stock & recipes',
    tabOnHand: 'On hand',
    tabMovements: 'Movements',
    tabRecipes: 'Recipes',
    tabMaterials: 'Materials',
    colKind: 'Kind',
    colCode: 'Code',
    colName: 'Name',
    colUom: 'Unit',
    colOnHand: 'On hand',
    colTheoretical: 'From recipe maths',
    colDate: 'Date',
    colType: 'Movement',
    colQty: 'Qty',
    colReason: 'Reason',
    colOrder: 'Bill',
    colActor: 'By',
    colProduct: 'Product',
    colYield: 'Yield',
    colLines: 'Materials',
    colNote: 'Note',
    pickOutlet: 'Pick an outlet',
    pickOutletFirst: 'Pick an outlet first — stock balances always belong to one outlet.',
    receive: 'Receive goods',
    waste: 'Record waste',
    addMaterial: 'Add material',
    addRecipe: 'Create a recipe for\u2026',
    addLine: 'material',
    recipeTitle: 'Recipe {{code}}',
    recipeDeleted: 'Recipe deleted because it had no materials',
    reasonRequired: 'Reason is required',
    theoretical: 'theoretical',
    coverageTitle: 'Recipes cover',
    coverageHelp:
      'Measured by sales VALUE, not menu count. Items without a recipe deduct nothing at all '
      + '\u2014 which is fine, as long as you can see it.',
    topUncovered: 'Not covered yet:',
    uomWarning:
      'One unit is used for recipes, receipts and stock counts alike. There is no automatic '
      + 'conversion \u2014 if the recipe uses grams, receive in grams too.',
    yieldHelp: 'How many portions one recipe yields. Enter 20 if one batch makes 20 buns.',
    emptyRecipeWarning:
      'A recipe with no materials is DELETED, not saved empty. An empty recipe makes the product '
      + 'count as "has a recipe" while deducting nothing.',
    kind: { product: 'Product', material: 'Material' },
    type: {
      receipt: 'Received',
      sale_backflush: 'Sold (recipe)',
      sale_direct: 'Sold (1:1)',
      waste: 'Waste',
      transfer_in: 'Transfer in',
      transfer_out: 'Transfer out',
      opname_adjustment: 'Count adjustment',
    },
    reason: {
      spoilage: 'Spoiled',
      breakage: 'Broken / spilled',
      expired: 'Expired',
      staff_meal: 'Staff meal',
      other: 'Other',
    },
  },
  report: {
    tab: 'Reports',
    salesTitle: 'Sales per day',
    kitchenTitle: 'Kitchen timing',
    station: 'Station',
    tickets: 'Tickets',
    avg: 'Average',
    late: 'Late',
    unfinished: 'Unfinished',
    itemTitle: 'Sales by item',
    drift: {
      title: 'Price drift (till vs master)',
      none: 'No drift — every bill was sold at master prices.',
      order: 'Bill',
      device: 'Device',
      product: 'Product',
      qty: 'Qty',
      charged: 'Charged',
      master: 'Master',
      delta: 'Difference',
      catalog: 'Catalog version',
    },
    item: 'Item',
    category: 'Category',
    qtySold: 'Sold',
    netSold: 'Net sales',
    refunded: 'Refunded',
    netAfterRefund: 'Net',
    share: 'Share',
    taxTitle: 'Tax recap (for filing)',
    taxCode: 'Code',
    taxName: 'Name',
    taxKind: 'Kind',
    inclusive: 'INCLUSIVE',
    rate: 'Rate',
    dpp: 'Taxable base',
    taxAmount: 'Tax',
    downloadCsv: 'Download CSV',
    taxBaseHint:
      'From {{bills}} PAID bills this period: gross Rp {{gross}}, total paid Rp {{grand}}. ' +
      'The taxable base below may be smaller than gross — tax-free fiscal classes and giveaways ' +
      'genuinely have no base.',
    taxMismatch:
      '⛔ The tax recap (Rp {{recap}}) does NOT match the tax total on the orders (Rp {{rollup}}). ' +
      'Do not copy these numbers into a filing until the cause is found — they are computed from ' +
      'different tables, and a gap means one of them is wrong.',
    tenderTitle: 'Takings by tender type',
    shiftTitle: 'Cash session close',
    date: 'Business date',
    outlet: 'Outlet',
    allOutlets: 'All outlets',
    refresh: 'Reload',
    bills: 'Bills',
    guests: 'Guests',
    gross: 'Gross',
    discount: 'Discount',
    serviceCharge: 'Service Charge',
    tax: 'Tax',
    grandTotal: 'Total',
    tenderTotal: 'Σ tender',
    tenderType: 'Type',
    count: 'Txn',
    amount: 'Taken',
    received: 'Cash received',
    change: 'Change',
    shiftNo: 'Session no.',
    openedBy: 'Opened by',
    status: 'Status',
    stillOpen: 'STILL OPEN',
    expectedCash: 'Expected cash',
    countedCash: 'Counted cash',
    variance: 'Variance',
    reason: 'Reason',
    summary: '{{bills}} bills · {{guests}} guests · Rp {{amount}}',
    imbalance:
      '⛔ {{n}} day(s) where tender takings do NOT equal sales. This should be impossible ' +
      '(Σ tender = bill total is enforced at close). Do not use these numbers until the cause is ' +
      'found — start with bills reopened that day.',
    businessDateHint:
      'Grouped by BUSINESS DATE — the day rolls over at cutover (04:00/05:00), not at midnight. ' +
      'A 01:00 bill still belongs to the previous day.',
  },
  receipt: {
    tab: 'Receipt',
    title: 'Receipt header & footer',
    pickOutlet: 'Pick an outlet',
    uploadLogo: 'Upload logo',
    removeLogo: 'Remove logo',
    logoHint:
      'Any PNG/JPG — converted to black-and-white at paper width on upload. Logos that are ' +
      'already crisp black-and-white look best; photos come out speckled on a thermal printer. ' +
      '⚠️ Change the paper width ⇒ upload the logo again.',
    logoTooWide:
      'This logo is wider than {{mm}} mm paper ⇒ the printer will SKIP it — the receipt comes out ' +
      'with no logo and no error. Upload it again while {{mm}} mm is selected.',
    address1: 'Address line 1',
    address2: 'Address line 2',
    footnote: 'Footnote (e.g. "Goods sold are not returnable")',
    save: 'Save',
    footnoteTitle: 'Footer lines',
    footnoteN: 'Line {{n}} — empty means not printed',
    fillExample: 'Fill example',
    fontTitle: 'Body font size',
    fontNormal: 'Normal',
    fontSmall: 'Small (fits more)',
    nameScaleTitle: 'Outlet name',
    scaleNormal: 'Normal',
    scaleLarge: 'Large',
    saved: 'Receipt template saved',
    preview: 'Preview · {{mm}} mm',
    previewHint:
      'The items and amounts are samples; real ones come from the order. The logo shows as a ' +
      'placeholder because this preview is text — the size printed here is what will be printed.',
    hint:
      'What you set here is printed on the guest receipt: logo and address at the top, footnote ' +
      'at the bottom. The preview is produced by the SAME code that drives the printer — what you ' +
      'see here is what comes out of the paper.',
  },
  integration: {
    tab: 'Integrations',
    title: 'Integration clients',
    subtitle:
      'Keys for reporting scripts, BI, and the MCP server. Every integration gets its OWN key — ' +
      'a shared key is a key nobody ever revokes.',
    scopeWhy:
      'Only READ scopes can be picked here. Platform, cashier and master-write scopes are absent ' +
      'on purpose — not because the screen is unfinished; the server refuses them.',
    add: 'New client',
    create: 'Create & show key',
    client: 'Client ID',
    scopes: 'Scopes',
    name: 'Name',
    nameHint: 'The server adds the "t:<tenant>:" prefix — the same name in another tenant will not clash.',
    nameRule: 'Lowercase letters, digits, hyphens; 3–40 characters.',
    agent: 'AI agent',
    agentQ: 'Is this an AI agent?',
    agentHint:
      'Its actions are recorded as "agent", not "system", so "what did the AI do?" has an answer. ' +
      'This is DATA, not a guess from the name.',
    keyTitle: 'Private key — shown once',
    keyOnce:
      'Copy it now. The server stores only the PUBLIC key, so this cannot be shown again and ' +
      'cannot be recovered. If it is lost: create a new client, then revoke this one.',
    keySaved: 'I have saved it',
    'scope.master:read': 'read menu, prices and taxes',
    'scope.reports:read': 'read reports and summaries',
    'scope.ops:read': 'read sync health and queues',
  },
  promo: {
    tabTarget: 'Targets',
    tabValidity: 'Validity',
    validityHint: 'Empty = always valid. The promo engine rejects out-of-window itself — so a seasonal promo need not be remembered and switched off.',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    timeFrom: 'From time',
    timeTo: 'To time',
    days: 'Days',
    daysHint: 'Empty = every day. Used for weekend or weekday promos.',
    daysAll: 'Every day',
    day: { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' },
    tabValue: 'Value & status',
    tab: 'Promotions',
    code: 'Code',
    name: 'Name',
    kind: 'Kind',
    value: 'Value',
    cap: 'Max discount',
    add: 'New promotion',
    scope: 'Scope',
    subtitle: 'Discounts belong to the brand. The cashier applies the value as-is — check it before saving.',
    frozen: 'Code, kind and scope cannot be changed: all three are already recorded on orders that went through.',
    capHint: 'Rupiah ceiling for a percentage promotion. Empty = no ceiling.',
    minSubtotal: 'Minimum spend',
    minSubtotalHint:
      'Computed from ELIGIBLE lines only — cigarettes and no-discount items do not count. ' +
      'Empty = no minimum.',
    needsApproval: 'Needs supervisor approval',
    needsApprovalHint:
      'The cashier is asked for the code and PIN of a supervisor holding "promo:approve" before ' +
      'this promotion applies, and the approver is recorded on the bill.',
    activeVsOutlet:
      'Inactive here means off at EVERY outlet of this brand. For a single outlet, use the switch in ' +
      'the "Active per outlet" column.',
    perOutlet: 'Active per outlet',
    pickOutlet: 'Pick an outlet to switch promotions on or off there.',
    note:
      'A brand promotion applies at ALL its outlets; only per-outlet EXCEPTIONS are stored. ' +
      'GoFood/Grab vouchers are NOT promotions — that money is billed to the provider, so it is a ' +
      'payment method (receivable), not a cut of our own revenue.',
  },
  common: { logout: 'Log out', language: 'Language', on: 'On', off: 'Off',
            save: 'Save', saved: 'Saved' },
};
